import React, { useCallback, useEffect, useState } from 'react';
import api from '../api/axios';
import { mediaSrc } from '../api/media';

interface ExposableKid { kidId: number; name: string; grade: number; profilePhotoUrl: string | null; exposed: boolean; }
interface VisibleKid { kidId: number; name: string; grade: number; profilePhotoUrl: string | null; parentId: number; parentName: string; }
interface ConnectionInfo { connectionId: number; otherKidId: number; otherKidName: string; otherKidPhotoUrl: string | null; }

interface Props {
  friendId: number;
  friendName: string;
  onClose: () => void;
}

const Avatar: React.FC<{ url: string | null; size?: number }> = ({ url, size = 28 }) => (
  url ? <img src={mediaSrc(url)} alt="" style={{ width: size, height: size }} className="rounded-full object-cover" />
      : <span style={{ width: size, height: size }} className="rounded-full bg-indigo-100 inline-flex items-center justify-center text-sm">🧒</span>
);

const SharingModal: React.FC<Props> = ({ friendId, friendName, onClose }) => {
  const [myKids, setMyKids] = useState<ExposableKid[]>([]);
  const [visibleKids, setVisibleKids] = useState<VisibleKid[]>([]);
  // map: myKidId -> { otherKidId -> connectionId }
  const [conns, setConns] = useState<Record<number, Record<number, number>>>({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mk, vk] = await Promise.all([
        api.get(`/api/parent/friends/${friendId}/my-kids`),
        api.get(`/api/parent/friends/${friendId}/visible-kids`),
      ]);
      setMyKids(mk.data);
      setVisibleKids(vk.data);
      // connections per exposed kid
      const exposed = (mk.data as ExposableKid[]).filter((k) => k.exposed);
      const entries = await Promise.all(exposed.map(async (k) => {
        const r = await api.get(`/api/parent/kids/${k.kidId}/connections`);
        const m: Record<number, number> = {};
        (r.data as ConnectionInfo[]).forEach((c) => { m[c.otherKidId] = c.connectionId; });
        return [k.kidId, m] as const;
      }));
      setConns(Object.fromEntries(entries));
    } catch {} finally { setLoading(false); }
  }, [friendId]);

  useEffect(() => { load(); }, [load]);

  const toggleExpose = async (k: ExposableKid) => {
    try {
      if (k.exposed) await api.delete(`/api/parent/friends/${friendId}/expose/${k.kidId}`);
      else await api.post(`/api/parent/friends/${friendId}/expose`, { kidId: k.kidId });
      await load();
    } catch (e: any) { alert(e.response?.data?.message || 'Failed'); }
  };

  const toggleConnect = async (myKidId: number, otherKidId: number) => {
    try {
      const existing = conns[myKidId]?.[otherKidId];
      if (existing) await api.delete(`/api/parent/connections/${existing}`);
      else await api.post('/api/parent/connections', { myKidId, otherKidId });
      await load();
    } catch (e: any) { alert(e.response?.data?.message || 'Failed'); }
  };

  const exposedKids = myKids.filter((k) => k.exposed);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-gray-700">Sharing with {friendName}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        {loading ? <p className="text-sm text-gray-400">Loading…</p> : (
          <div className="space-y-5">
            {/* Expose my kids */}
            <section>
              <p className="text-sm font-semibold text-gray-700 mb-2">Show my kids to {friendName}</p>
              <div className="space-y-2">
                {myKids.length === 0 && <p className="text-xs text-gray-400">You have no kids.</p>}
                {myKids.map((k) => (
                  <label key={k.kidId} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={k.exposed} onChange={() => toggleExpose(k)} />
                    <Avatar url={k.profilePhotoUrl} />
                    <span>{k.name} <span className="text-gray-400">· G{k.grade}</span></span>
                  </label>
                ))}
              </div>
            </section>

            {/* Connect to friend's kids */}
            <section>
              <p className="text-sm font-semibold text-gray-700 mb-1">{friendName}'s kids</p>
              <p className="text-xs text-gray-400 mb-2">
                Connect a kid below to your shared kids. Both sides must share first.
              </p>
              {visibleKids.length === 0 ? (
                <p className="text-xs text-gray-400">{friendName} hasn't shared any kids with you yet.</p>
              ) : (
                <div className="space-y-3">
                  {visibleKids.map((vk) => (
                    <div key={vk.kidId} className="border border-gray-100 rounded-xl p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Avatar url={vk.profilePhotoUrl} />
                        <span className="text-sm font-medium text-gray-700">{vk.name} <span className="text-gray-400">· G{vk.grade}</span></span>
                      </div>
                      {exposedKids.length === 0 ? (
                        <p className="text-xs text-amber-600">Share one of your kids above to connect them.</p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {exposedKids.map((mk) => {
                            const connected = !!conns[mk.kidId]?.[vk.kidId];
                            return (
                              <button key={mk.kidId} onClick={() => toggleConnect(mk.kidId, vk.kidId)}
                                className={`text-xs px-2.5 py-1 rounded-full border transition ${
                                  connected ? 'bg-green-100 border-green-300 text-green-700'
                                            : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'}`}>
                                {connected ? '✓ ' : '+ '}{mk.name}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default SharingModal;
